import * as Sentry from "@sentry/browser";
import { API_BASE_URL, TOKENS_VERSION, isProd } from "config/env";
import { chainInfoResponseSchema, pMMTokenListSchema } from "generated-schemas";
import { pricesByChainResponseSchema } from "generated-schemas-tokens";
import { size } from "lodash-es";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { Address, Hash } from "viem";
import { Chain } from "viem/chains";
import { z } from "zod";
import { Amount } from "../../types/amount";
import { transformPricesToBalances } from "../../utils/tokenListUtils";
import { GlobalStore, TokenListByChain } from "./GlobalStore";
import { CHAINS } from "config/chains";

type TokenType = "alt" | "stablecoin" | "major";

/**
 * 0 - No permit
 * 1 - Permit (Domain with version and chainId)
 * 2 - Permit (Domain without version, but with chainId )
 * 3 - Permit (Domain with version and salt)
 * 4 - Permit (Domain with version, chainid and salt) (
 */
export enum PermitDomain {
  NONE = "NONE",
  WITH_VERSION_AND_CHAINID = "WITH_VERSION_AND_CHAINID",
  WITH_CHAINID = "WITH_CHAINID",
  WITH_VERSION_AND_SALT = "WITH_VERSION_AND_SALT",
  WITH_VERSION_AND_CHAINID_AND_SALT = "WITH_VERSION_AND_CHAINID_AND_SALT",
}

export interface TokenData {
  name: string;
  ticker: string;
  tokenType: Array<TokenType>;
  permitDomain: PermitDomain;
  iconUrl: string;
  colour: string;
  priceUsd: Amount;
  chainInfo: ChainSupport;
  displayDecimals: number | null;
}

export type TokenList = {
  [key: Hash]: TokenData;
};

export type ChainSupport = z.TypeOf<typeof chainInfoResponseSchema>;

export interface TokenListResponse {
  tokens: TokenList;
  metadata: {
    lastUpdate: number;
  };
}

export const fetchTokens = async (
  chainId: Chain["id"],
  includeInactiveTokens: boolean
): Promise<unknown> => {
  const chainName = CHAINS[chainId].apiName;
  let tokenlistUrl = `${API_BASE_URL}/tokens/${TOKENS_VERSION}/${chainName}/tokenlist`;

  // UAT-only
  if (GlobalStore.getRawState().isCustomTokenList && !isProd) {
    tokenlistUrl = GlobalStore.getRawState().customTokenListUrl;
  }

  try {
    const tokenList = await fetch(tokenlistUrl);
    if (tokenList) {
      return tokenList.json();
    } else {
      throw "Could not fetch token metadata.";
    }
  } catch (error) {
    Sentry.captureException(error);
  }
};

const fetchTokenPrices = async (chainName: Chain["name"]) => {
  try {
    const prices = await fetch(`${API_BASE_URL}/tokens/${TOKENS_VERSION}/${chainName}/prices`);
    if (prices.ok) {
      return prices.json();
    } else {
      throw "Could not fetch token metadata.";
    }
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
};

const parsePermitDomain = (permitDomain: number): PermitDomain => {
  switch (permitDomain) {
    case 0:
      return PermitDomain.NONE;
    case 1:
      return PermitDomain.WITH_VERSION_AND_CHAINID;
    case 2:
      return PermitDomain.WITH_CHAINID;
    case 3:
      return PermitDomain.WITH_VERSION_AND_SALT;
    case 4:
      return PermitDomain.WITH_VERSION_AND_CHAINID_AND_SALT;
    default:
      return PermitDomain.NONE;
  }
};

const transformTokenListResponse = (
  tokenListResponse: z.infer<typeof pMMTokenListSchema>,
  pricesResponse: z.infer<typeof pricesByChainResponseSchema> | null,
  chainId: Chain["id"]
): TokenList => {
  const metadata = Object.fromEntries(
    tokenListResponse.tokens
      .filter(value => value.chainId === chainId)
      .map(value => {
        let priceUsd = new Amount("0");
        try {
          if (pricesResponse != null && pricesResponse[value.address] !== null) {
            priceUsd = new Amount(pricesResponse[value.address]?.toString() ?? "0");
          }
        } catch (e) {
          console.error(`Could not parse priceUsd for token ${value.symbol}`, e);
        }
        const DEFAULT_DISPLAY_DECIMALS = 5;
        if (!value.extensions?.displayDecimals) {
          Sentry.captureException(`Token ${value.symbol} does not have displayDecimals set`);
        }
        const permitDomainForChain = CHAINS[value.chainId].permitDomains;
        const tokenData: TokenData = {
          name: value.name,
          ticker: value.symbol,
          tokenType: value.tags as Array<TokenType>,
          permitDomain: parsePermitDomain(
            value.address in permitDomainForChain
              ? permitDomainForChain[value.address as Address]
              : 0
          ),
          iconUrl: value.logoURI,
          colour: value.extensions?.color ?? "#ffffff",
          priceUsd: priceUsd,
          chainInfo: {
            chainId: value.chainId,
            contractAddress: value.address as Address,
            decimals: value.decimals,
          },
          displayDecimals: value.extensions?.displayDecimals ?? DEFAULT_DISPLAY_DECIMALS,
        };
        return [value.address, tokenData];
      })
  );
  return metadata;
};

const validateTokenListResponse = (tokenListResponse: unknown) => {
  const result = pMMTokenListSchema.safeParse(tokenListResponse);
  return result;
};

const validatePricesResponse = (pricesResponse: unknown) => {
  const result = pricesByChainResponseSchema.safeParse(pricesResponse);
  return result;
};

/*
 * 1. Fetch tokens
 * 2. Validate response
 *  2.1 If invalid, return null
 *  2.2 If valid, transform response
 */
export const fetchTokenList = async (chainId: Chain["id"], includeInactiveTokens: boolean) => {
  let tokenList;
  try {
    tokenList = await fetchTokens(chainId, includeInactiveTokens);
  } catch (error) {
    console.error(error);
    throw Error(typeof error === "string" ? error : "fetchTokens failed");
  }

  let prices = null;
  try {
    prices = await fetchTokenPrices(CHAINS[chainId].apiName);
  } catch (error) {
    // Do not throw, treat all prices as '0'
    console.error(error);
  }
  const tokenListValidationResult = validateTokenListResponse(tokenList);
  const pricesValidationResult = validatePricesResponse(prices);

  if (tokenListValidationResult.success) {
    const metadata = tokenListValidationResult.data;
    const pricesData = pricesValidationResult.success ? pricesValidationResult.data : null;
    // Potentially there might be issues with transforming it even if it's a valid response?
    const tokensMetadata = transformTokenListResponse(metadata, pricesData, chainId);

    const chainFilteredTokensMetadata = Object.entries(tokensMetadata).reduce<TokenList>(
      (filteredMetadata, [key, value]) => {
        if (value.chainInfo.chainId === chainId) {
          return Object.assign({}, filteredMetadata, {
            [key]: value,
          });
        } else {
          return filteredMetadata;
        }
      },
      {}
    );
    return chainFilteredTokensMetadata;
  }

  if (!tokenListValidationResult.success) {
    console.log(
      `Invalid response from token list endpointError msg: ${JSON.stringify(
        tokenListValidationResult.error.format()
      )}`
    );
    return null;
  }
  if (!pricesValidationResult.success) {
    console.error(
      `Invalid response from prices endpoint. Error msg: ${JSON.stringify(
        pricesValidationResult.error.format()
      )}`
    );
    return null;
  }
};

export const fetchAllTokensLists = async (): Promise<TokenListByChain> => {
  const chainIds = Object.keys(CHAINS);
  const results = await Promise.allSettled(
    chainIds.map(chainId => fetchTokenList(Number(chainId), false))
  );
  const tokens: TokenListByChain = {};
  results
    .map(result => (result.status === "fulfilled" ? result.value : {}))
    .forEach((result_1, i) => {
      Object.assign(tokens, { [chainIds[i]]: result_1 });
    });
  return tokens;
};

export const getAllTokenLists = createAsyncAction(
  async () => {
    try {
      const result = await fetchAllTokensLists();
      if (result != null) {
        return successResult(result);
      }
      throw new Error(`Returned result is falsy`);
    } catch (e) {
      console.log(e);
      Sentry.captureException(`Could not get tokens`, { extra: { event: e } });
      return errorResult([], `Could not get all token lists`, e);
    }
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error) {
        GlobalStore.update(s => {
          if (size(result.payload) === 0) {
            console.log(`Got an empty response from the Tokens endpoint - dropping it`);
            Sentry.captureException(`tokens endpoint returned an empty response`);
            return;
          }
          s.tokenLists = result.payload;

          // TODO: This should be a side-effect in reaction, or we could restructure s.balances
          // It's weird for this to be here since its chain-dependent
          if (s.balances.length === 0) {
            const tokenListForCurrentChain = result.payload[s.network.id];
            s.balances = transformPricesToBalances(tokenListForCurrentChain);
          }
        });
      } else {
        console.error(`Error fetching token lists: ${result.error}`);
      }
    },
  }
);
